import React, { useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  useDeleteReplyMutation,
  useEditReplyMutation,
  useGetBlogDetailsQuery,
  useGetBlogsQuery,
  useReplyToCommentMutation,
  useToggleLikeCommentMutation,
} from "../../redux/api/blogsApi";
import Loader from "../layout/Loader";
import DOMPurify from "dompurify";
import { Helmet } from "react-helmet-async";
import NewComment from "../comments/NewComment";
import { useSelector } from "react-redux";

import { getReadingTime } from "../../helpers/helpers";
import ShareBar from "./ShareBar";
import CommentCard from "./CommentCard";

const BlogDetailsPage = () => {
  const params = useParams();
  const { data, isLoading, error } = useGetBlogDetailsQuery(params?.id);
  const { data: blogsData } = useGetBlogsQuery();
  const [toggleLikeComment] = useToggleLikeCommentMutation();
  const [replyToComment] = useReplyToCommentMutation();
  const [deleteReply] = useDeleteReplyMutation();
  const [editReply] = useEditReplyMutation();

  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const currentUserId = user?._id;
  const commentsRef = useRef(null);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const blog = data?.blog;
  const blogId = blog?._id;

  const handleToggleLike = async (commentId) => {
    if (!isAuthenticated || !blogId) return;
    try {
      await toggleLikeComment({ blogId, commentId });
    } catch {}
  };

  const handleReply = async (commentId, text) => {
    if (!isAuthenticated || !text.trim() || !blogId) return;
    try {
      await replyToComment({ blogId, commentId, text });
    } catch {}
  };

  const handleDelete = async (commentId, replyId) => {
    if (!blogId) return;
    try {
      await deleteReply({ blogId, commentId, replyId }).unwrap();
    } catch {}
  };

  const handleEdit = async (commentId, replyId, text) => {
    if (!text.trim() || !blogId) return;
    try {
      await editReply({ blogId, commentId, replyId, text });
    } catch {}
  };

  if (isLoading) return <Loader />;
  if (error)
    return (
      <p className="text-red-500 text-center mt-20 font-sourcesans">
        {error?.data?.message || "Failed to load blog"}
      </p>
    );

  if (!blog)
    return (
      <p className="text-stone-700 text-center mt-20 font-sourcesans">
        Blog not found.
      </p>
    );

  const readingTime = getReadingTime(blog.description);

  const wasUpdated =
    blog.contentUpdatedAt &&
    new Date(blog.contentUpdatedAt) - new Date(blog.createdAt) > 60000;

  const allBlogs = blogsData?.blogs || [];
  const relatedBlogs = allBlogs
    .filter((b) => b._id !== blog._id && b.category === blog.category)
    .slice(0, 3);

  return (
    <>
      <Helmet>
        <title>{blog.name} — Sanguine Accent</title>
        <meta
          name="description"
          content={
            blog.metaDescription ||
            DOMPurify.sanitize(blog.description, { ALLOWED_TAGS: [] }).slice(
              0,
              155
            )
          }
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href={`https://sanguineaccent.com/blogs/${blog._id}`}
        />
        <meta property="og:title" content={blog.name} />
        <meta
          property="og:description"
          content={
            blog.metaDescription ||
            DOMPurify.sanitize(blog.description, { ALLOWED_TAGS: [] }).slice(
              0,
              155
            )
          }
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content={`https://sanguineaccent.com/blogs/${blog._id}`}
        />
        {blog.images?.[0]?.url && (
          <meta property="og:image" content={blog.images[0].url} />
        )}
      </Helmet>

      <motion.article
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-24 lg:mt-44 pb-16"
      >
        {blog.images?.[0]?.url && (
          <img
            src={blog.images[0].url}
            alt={blog.name}
            className="w-full h-auto object-cover rounded-xl shadow-sm mb-8"
            loading="eager"
            fetchPriority="high"
            decoding="sync"
          />
        )}

        <div className="mb-4">
          <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-sourcesans text-stone-500 bg-stone-100 px-3 py-1 rounded-full">
            {blog.category}
          </span>
        </div>

        <h1
          className="font-playfair font-medium text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-stone-900 leading-tight mb-5"
          style={{ textWrap: "balance" }}
        >
          {blog.name}
        </h1>

        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-stone-400 font-sourcesans mb-8"
          style={{ textWrap: "pretty" }}
        >
          <span className="text-stone-600 font-medium">
            {blog.author?.name || blog.author}
          </span>
          <span aria-hidden="true">·</span>
          <span>
            {new Date(blog.createdAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </span>
          {wasUpdated && (
            <>
              <span aria-hidden="true">·</span>
              <span className="italic">
                Updated{" "}
                {new Date(blog.contentUpdatedAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            </>
          )}
          <span aria-hidden="true">·</span>
          <span>{readingTime} min read</span>
          <span aria-hidden="true">·</span>
          <span>{blog.views || 0} views</span>
          <button
            onClick={() =>
              commentsRef.current?.scrollIntoView({ behavior: "smooth" })
            }
            className="hover:text-[#BC3F4A] transition"
          >
            {blog.numOfComments}{" "}
            {blog.numOfComments === 1 ? "comment" : "comments"}
          </button>
        </div>

        <div
          className="blog-content prose prose-stone prose-sm sm:prose-base max-w-none"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(blog.description),
          }}
        />

        <div className="mt-10 pt-6 border-t border-stone-200">
          <ShareBar
            url={shareUrl}
            title={blog.name}
            imageUrl={blog.images?.[0]?.url}
          />
        </div>

        <section
          className="mt-10 pt-6 border-t border-stone-200"
          ref={commentsRef}
        >
          <h2 className="text-2xl font-bold text-stone-900 font-sourcesans mb-6">
            {blog.numOfComments === 0
              ? "No comments yet"
              : `${blog.numOfComments} ${
                  blog.numOfComments === 1 ? "Comment" : "Comments"
                }`}
          </h2>

          {isAuthenticated ? (
            <NewComment
              blogId={blog._id}
              userAvatar={user?.avatar?.url}
              userName={user?.name}
            />
          ) : (
            <div className="text-sm text-stone-600 border border-stone-200 rounded-md p-4 text-center font-sourcesans mb-6">
              <Link
                to="/login"
                className="text-[#BC3F4A] font-medium hover:underline"
              >
                Log in
              </Link>{" "}
              to leave a comment.
            </div>
          )}

          <div className="mt-6 space-y-6 divide-y divide-stone-100">
            {blog.comments
              .slice()
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .map((rev) => (
                <div key={rev._id} className="pt-6 first:pt-0">
                  <CommentCard
                    rev={rev}
                    currentUserId={currentUserId}
                    isAuthenticated={isAuthenticated}
                    blog={blog}
                    onToggleLike={handleToggleLike}
                    onReply={handleReply}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                </div>
              ))}
          </div>
        </section>

        {relatedBlogs.length > 0 && (
          <section className="mt-12 pt-6 border-t border-stone-200">
            <h2 className="text-sm uppercase tracking-widest text-stone-400 font-sourcesans mb-5">
              More in {blog.category}
            </h2>
            <div className="grid gap-4 sm:grid-cols-3">
              {relatedBlogs.map((rel) => (
                <Link
                  key={rel._id}
                  to={`/blogs/${rel._id}`}
                  className="group block rounded-lg border border-stone-200 bg-white/70 overflow-hidden hover:shadow-md transition-shadow duration-200"
                >
                  {rel.images?.[0]?.url && (
                    <div className="aspect-video overflow-hidden bg-stone-100">
                      <img
                        src={rel.images[0].url}
                        alt={rel.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-3">
                    <h3
                      className="text-sm font-medium text-stone-800 line-clamp-2 group-hover:text-[#BC3F4A] transition-colors"
                      style={{ textWrap: "pretty" }}
                    >
                      {rel.name}
                    </h3>
                    <p className="text-xs text-stone-400 font-sourcesans mt-1">
                      {new Date(rel.createdAt).toLocaleDateString()} ·{" "}
                      {getReadingTime(rel.description)} min read
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-10 text-center">
          <Link
            to="/blogs"
            className="text-sm font-sourcesans text-stone-500 hover:text-[#BC3F4A] underline underline-offset-4 transition"
          >
            ← Back to all posts
          </Link>
        </div>
      </motion.article>
    </>
  );
};

export default BlogDetailsPage;
